import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ONBOARDING_STEPS } from "@/lib/onboardingSteps";
import { FieldRenderer } from "@/components/onboarding/FieldRenderer";
import { ArrowLeft, ArrowRight, Check, Loader2, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/acq`;

export default function OnboardingPublic() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [step, setStep] = useState(1);
  const [data, setData] = useState({});
  const [assets, setAssets] = useState([]);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [consent, setConsent] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    axios.get(`${API}/onboarding/${token}`)
      .then(({ data: d }) => {
        setMeta(d);
        setAssets(d.assets || []);
        setData(d.step_data || {});
        setStep(Math.min(d.current_step || 1, ONBOARDING_STEPS.length));
        if (d.completed_at) setDone(true);
      })
      .catch((e) => setError(e.response?.data?.detail || "Lien invalide"));
  }, [token]);

  const stepDef = ONBOARDING_STEPS[step - 1];
  const stepData = data[step] || {};
  const visibleFields = useMemo(
    () => (stepDef?.fields || []).filter((f) => !f.showIf || f.showIf(stepData)),
    [stepDef, stepData]
  );

  const persist = async (s, payload) => {
    setSaving(true);
    try {
      await axios.put(`${API}/onboarding/${token}/step`, { step: s, data: payload });
    } catch {
      toast.error("Sauvegarde impossible, vérifiez votre connexion");
    } finally {
      setSaving(false);
    }
  };

  const onChange = (name, value) => {
    const next = { ...stepData, [name]: value };
    setData((d) => ({ ...d, [step]: next }));
    clearTimeout(timer.current);
    timer.current = setTimeout(() => persist(step, next), 900);
  };

  const onUpload = async (file, kind) => {
    const form = new FormData();
    form.append("file", file);
    form.append("kind", kind);
    try {
      const { data: asset } = await axios.post(`${API}/onboarding/${token}/upload`, form);
      setAssets((a) => [...a, asset]);
      toast.success(`${file.name} envoyé`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Envoi impossible");
    }
  };

  const go = async (dir) => {
    clearTimeout(timer.current);
    await persist(step, stepData);
    const next = step + dir;
    if (next >= 1 && next <= ONBOARDING_STEPS.length + 1) setStep(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    try {
      await axios.post(`${API}/onboarding/${token}/submit`, { consent: true });
      setDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Envoi impossible");
    }
  };

  if (error) return <Shell><h1 className="font-display text-3xl text-brand-ink">{error}</h1>
    <p className="mt-4 text-brand-subtle">Contactez votre interlocuteur chez Instan.t pour recevoir un nouveau lien.</p></Shell>;

  if (!meta) return <Shell><Loader2 className="h-6 w-6 animate-spin text-brand-moss" /></Shell>;

  if (done) return (
    <Shell>
      <div data-testid="onboarding-done">
        <Check className="h-9 w-9 text-brand-moss" strokeWidth={1.5} />
        <h1 className="mt-6 font-display text-4xl sm:text-5xl text-brand-ink leading-[1.08]">
          Merci{meta.contact_name ? ` ${meta.contact_name.split(" ")[0]}` : ""}, nous avons tout ce qu'il nous faut.
        </h1>

        <div className="mt-14 eyebrow">Voici ce que nous allons préparer pour vous</div>
        <div className="mt-6 divide-y divide-brand-line border-t border-b border-brand-line">
          {[
            "Analyse de votre entreprise",
            "Stratégie d'acquisition",
            "Préparation de vos campagnes",
            "Mise en place du suivi des résultats",
          ].map((label, i) => (
            <div key={label} className="flex items-baseline gap-6 py-5" data-testid={`next-step-${i + 1}`}>
              <span className="font-mono text-[11px] text-brand-subtle">{String(i + 1).padStart(2, "0")}</span>
              <span className="text-base text-brand-ink">{label}</span>
            </div>
          ))}
        </div>

        <p className="mt-12 text-base text-brand-subtle max-w-[50ch]">
          L'équipe Agence Instant prend le relais à partir d'ici. Nous revenons vers vous dès que
          votre stratégie est prête.
        </p>
      </div>
    </Shell>
  );

  const isRecap = step === ONBOARDING_STEPS.length + 1;
  const percent = Math.round(((isRecap ? ONBOARDING_STEPS.length : step - 1) / ONBOARDING_STEPS.length) * 100);

  return (
    <Shell>
      <div className="flex items-center justify-between gap-4">
        <div className="eyebrow" data-testid="onboarding-step-indicator">
          {isRecap ? "Récapitulatif" : `Étape ${step} sur ${ONBOARDING_STEPS.length}`}
        </div>
        <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-brand-subtle">
          {saving ? "Sauvegarde…" : "Sauvegarde automatique"}
        </div>
      </div>
      <div className="mt-3 h-1 w-full bg-brand-line/60 rounded-full overflow-hidden">
        <div className="h-full bg-brand-moss transition-all duration-500" style={{ width: `${percent}%` }} />
      </div>

      {isRecap ? (
        <div className="mt-12" data-testid="onboarding-recap">
          <h1 className="font-display text-3xl sm:text-4xl text-brand-ink">Tout est prêt</h1>
          <p className="mt-4 text-base text-brand-subtle max-w-[52ch]">
            Vérifiez rapidement vos réponses, puis validez pour nous les transmettre.
          </p>
          <div className="mt-10 space-y-4">
            {ONBOARDING_STEPS.map((s, i) => {
              const filled = Object.values(data[i + 1] || {}).filter((v) =>
                Array.isArray(v) ? v.length : v !== "" && v !== undefined && v !== null).length;
              return (
                <div key={s.key} className="flex items-center justify-between gap-4 border-b border-brand-line pb-3">
                  <div>
                    <div className="text-sm text-brand-ink">{s.title}</div>
                    <div className="text-[11px] font-mono text-brand-subtle mt-0.5">{filled} réponse(s)</div>
                  </div>
                  <button onClick={() => setStep(i + 1)} data-testid={`recap-edit-${i + 1}`}
                    className="text-[10px] font-mono uppercase tracking-[0.14em] text-brand-moss hover:underline">
                    Modifier
                  </button>
                </div>
              );
            })}
          </div>

          <label className="mt-10 flex items-start gap-3 text-sm text-brand-ink/85 cursor-pointer">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
              data-testid="onboarding-consent" className="mt-1 accent-brand-moss h-4 w-4" />
            <span>
              J'autorise Agence Instant à utiliser ces informations pour créer et gérer mes campagnes
              d'acquisition, et je confirme qu'elles sont exactes.
            </span>
          </label>

          <div className="mt-10 flex items-center gap-3">
            <Button variant="outline" onClick={() => setStep(ONBOARDING_STEPS.length)}
              className="border-brand-line rounded-md" data-testid="onboarding-back">
              <ArrowLeft className="h-4 w-4 mr-2" /> Retour
            </Button>
            <Button onClick={submit} disabled={!consent} data-testid="onboarding-submit"
              className="bg-brand-ink hover:bg-brand-moss text-white rounded-md">
              Terminer mon onboarding <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-12" data-testid={`onboarding-step-${step}`}>
          <h1 className="font-display text-3xl sm:text-4xl text-brand-ink">{stepDef.title}</h1>
          <p className="mt-4 text-base text-brand-subtle max-w-[54ch]">{stepDef.intro}</p>

          <div className="mt-10 space-y-7">
            {visibleFields.map((f) => (
              <FieldRenderer key={f.name} field={f} value={stepData[f.name]}
                onChange={onChange} onUpload={onUpload} assets={assets} />
            ))}
          </div>

          <div className="mt-12 flex items-center justify-between gap-3">
            <Button variant="outline" onClick={() => go(-1)} disabled={step === 1}
              className="border-brand-line rounded-md" data-testid="onboarding-back">
              <ArrowLeft className="h-4 w-4 mr-2" /> Retour
            </Button>
            <Button onClick={() => go(1)} data-testid="onboarding-next"
              className="bg-brand-ink hover:bg-brand-moss text-white rounded-md">
              Continuer <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      <div className="mt-16 flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.14em] text-brand-subtle/70">
        <ShieldCheck className="h-3.5 w-3.5" /> Vos réponses sont enregistrées automatiquement — vous pouvez revenir plus tard
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-brand-paper">
      <div className="max-w-[760px] mx-auto px-6 sm:px-10 py-14 sm:py-20">
        <div className="flex items-center gap-4 mb-14">
          <img src="/picto-instant.png" alt="Instan.t" className="h-9 w-9 object-contain" />
          <div className="h-9 w-px bg-brand-line" />
          <div className="leading-none">
            <div className="font-display text-xl text-brand-ink">Studio</div>
            <div className="eyebrow mt-1.5">by Instan.t</div>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}
